import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { X, Upload, Trash2, Check, Eye, EyeOff, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';

const PAGES = [
  { key: 'work', label: 'My work' },
  { key: 'journal', label: 'Reflection journal' },
  { key: 'journey', label: 'Consciousness Progression' },
  { key: 'calendar', label: 'My calendar' },
  { key: 'values', label: 'Values' },
  { key: 'rituals', label: 'Sacred Habit Stack' },
  { key: 'milestones', label: 'My milestones' },
  { key: 'insights', label: 'My insights' },
  { key: 'keepsake', label: 'Mine to keep' },
];

const Section = ({ title, hint, children }) => (
  <section className="space-y-3 border-t border-white/10 pt-5 first:border-0 first:pt-0">
    <div>
      <h3 className="text-sm font-medium text-white">{title}</h3>
      {hint && <p className="text-[11px] text-white/40 mt-0.5">{hint}</p>}
    </div>
    {children}
  </section>
);

const Swatch = ({ url, name, chosen, onPick, onDelete }) => (
  <div className="relative group">
    <button onClick={onPick}
            className={`block w-full aspect-[4/3] rounded-lg border overflow-hidden
                        transition-colors ${chosen
                          ? 'border-amber-300/80' : 'border-white/12 hover:border-white/35'}`}
            data-testid={`background-option-${name}`}>
      {url
        ? <img src={url} alt={name} className="w-full h-full object-cover" />
        : <span className="flex items-center justify-center w-full h-full text-[10px]
                           text-white/45">None</span>}
    </button>
    {chosen && (
      <Check className="absolute top-1 right-1 w-4 h-4 text-amber-200 drop-shadow" />
    )}
    {onDelete && (
      <button onClick={onDelete}
              className="absolute bottom-1 right-1 hidden rounded bg-black/60 p-1
                         text-white/70 hover:text-rose-300 group-hover:block"
              data-testid={`background-delete-${name}`}>
        <Trash2 className="w-3 h-3" />
      </button>
    )}
  </div>
);

/** Their Companion, their space: background, mandala colours, naming, and which cards show. */
export const CompanionSettings = ({ settings, onSaved, onClose }) => {
  const [state, setState] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const save = async (changes) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/companion/settings`, changes);
      setState(data);
      onSaved(data);
    } catch {
      toast.error('That could not be saved');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await axios.post(`${API}/upload-photo`, body,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const { data: saved } = await axios.post(`${API}/companion/settings/uploads`,
        { url: data.file_path });
      setState(saved);
      onSaved(saved);
      await save({ background: data.file_path });
      toast.success('Your background is in place');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That image could not be uploaded');
    } finally {
      setBusy(false);
    }
  };

  const removeUpload = async (url) => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/companion/settings/uploads`, { data: { url } });
      setState(data);
      onSaved(data);
    } finally {
      setBusy(false);
    }
  };

  const order = state.card_order?.length
    ? state.cards.slice().sort((a, b) => {
        const ai = state.card_order.indexOf(a.key);
        const bi = state.card_order.indexOf(b.key);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      })
    : state.cards;

  const move = (key, direction) => {
    const keys = order.map((c) => c.key);
    const index = keys.indexOf(key);
    const swap = index + direction;
    if (swap < 0 || swap >= keys.length) return;
    [keys[index], keys[swap]] = [keys[swap], keys[index]];
    save({ card_order: keys });
  };

  const toggleCard = (key) => {
    const hidden = state.hidden_cards.includes(key)
      ? state.hidden_cards.filter((k) => k !== key)
      : [...state.hidden_cards, key];
    save({ hidden_cards: hidden });
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center
                    bg-slate-950/85 backdrop-blur-sm" data-testid="companion-settings">
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl
                      sm:rounded-2xl border border-white/10 bg-[#0b1020] p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-300/70">
              Make it yours
            </p>
            <h2 className="text-2xl font-bold text-white mt-1">Companion settings</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                  data-testid="companion-settings-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <Section title="Background" hint="Applies to every page of your Companion.">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            <Swatch name="none" url="" chosen={!state.background}
                    onPick={() => save({ background: '' })} />
            {state.presets.map((preset) => (
              <Swatch key={preset.key} name={preset.key} url={preset.url}
                      chosen={state.background === preset.key}
                      onPick={() => save({ background: preset.key })} />
            ))}
            {state.uploads.map((url) => (
              <Swatch key={url} name={url.slice(-12)} url={url}
                      chosen={state.background === url}
                      onPick={() => save({ background: url })}
                      onDelete={() => removeUpload(url)} />
            ))}
          </div>
          <label className="inline-flex items-center gap-2 rounded-full border border-white/15
                            px-4 py-2 text-xs text-white/70 hover:text-white cursor-pointer
                            transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload your own
            <input type="file" accept="image/*" className="hidden"
                   onChange={(e) => upload(e.target.files?.[0])}
                   data-testid="background-upload" />
          </label>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Softness behind the cards</span>
              <span className="text-xs text-white/70">{state.scrim}%</span>
            </div>
            <input type="range" min="0" max="90" value={state.scrim}
                   onChange={(e) => setState({ ...state, scrim: Number(e.target.value) })}
                   onMouseUp={(e) => save({ scrim: Number(e.target.value) })}
                   onTouchEnd={(e) => save({ scrim: Number(e.target.value) })}
                   className="w-full accent-amber-400" data-testid="scrim-slider" />
            <p className="text-[11px] text-white/30">
              Raise this if a lighter image ever competes with the text.
            </p>
          </div>

          <button onClick={() => setAdvanced(!advanced)}
                  className="text-[11px] text-cyan-200/80 hover:text-cyan-100 transition-colors"
                  data-testid="toggle-page-backgrounds">
            {advanced ? 'Hide' : 'Use a different image on certain pages'}
          </button>
          {advanced && (
            <div className="space-y-2 rounded-xl border border-white/10 bg-black/20 p-3"
                 data-testid="page-backgrounds">
              {PAGES.map((page) => (
                <div key={page.key} className="flex items-center gap-2">
                  <span className="flex-1 text-xs text-white/60">{page.label}</span>
                  <select
                    value={state.page_backgrounds[page.key] || ''}
                    onChange={(e) => {
                      const next = { ...state.page_backgrounds };
                      if (e.target.value) next[page.key] = e.target.value;
                      else delete next[page.key];
                      save({ page_backgrounds: next });
                    }}
                    className="rounded-lg border border-white/12 bg-black/40 px-2 py-1 text-[11px]
                               text-white/70"
                    data-testid={`page-background-${page.key}`}>
                    <option value="">Same as everywhere</option>
                    {state.presets.map((p) => (
                      <option key={p.key} value={p.key}>{p.name}</option>
                    ))}
                    {state.uploads.map((url, i) => (
                      <option key={url} value={url}>My image {i + 1}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Mandala colours" hint="Morning, midday and evening, in your own palette.">
          <div className="flex flex-wrap gap-2">
            {Object.entries(state.palettes).map(([key, palette]) => (
              <button key={key} onClick={() => save({ palette: key })}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1.5
                                  text-xs transition-colors ${state.palette === key
                                    ? 'border-amber-300/60 bg-amber-300/10 text-amber-100'
                                    : 'border-white/15 text-white/60 hover:border-white/35'}`}
                      data-testid={`palette-${key}`}>
                <span className="flex gap-0.5">
                  {palette.tones.map((t) => (
                    <span key={t} className="w-2.5 h-2.5 rounded-full"
                          style={{ background: t }} />
                  ))}
                </span>
                {palette.name}
              </button>
            ))}
          </div>
        </Section>

        <Section title="Your space" hint="What this place is called, and how it greets you.">
          <input value={state.space_name}
                 onChange={(e) => setState({ ...state, space_name: e.target.value })}
                 onBlur={(e) => save({ space_name: e.target.value })}
                 placeholder="Consciousness Companion"
                 className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2
                            text-sm text-white placeholder-white/25 focus:outline-none
                            focus:border-amber-300/60"
                 data-testid="space-name-input" />
          <input value={state.opening_line}
                 onChange={(e) => setState({ ...state, opening_line: e.target.value })}
                 onBlur={(e) => save({ opening_line: e.target.value })}
                 placeholder="An intention or line to meet you here"
                 className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2
                            text-sm text-white placeholder-white/25 focus:outline-none
                            focus:border-amber-300/60"
                 data-testid="opening-line-input" />
        </Section>

        <Section title="Veil"
                 hint="How your background sits behind everything — deep night, or lifted and soft.">
          <div className="flex gap-2">
            {[['dark', 'Night'], ['soft', 'Soft']].map(([key, label]) => (
              <button key={key} onClick={() => save({ theme: key })}
                      className={`rounded-full border px-4 py-1.5 text-xs transition-colors
                        ${state.theme === key
                          ? 'border-amber-300/60 bg-amber-300/10 text-amber-100'
                          : 'border-white/15 text-white/60 hover:border-white/35'}`}
                      data-testid={`theme-${key}`}>
                {label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="Your cards" hint="Hide what you do not use, and set the order you meet them in.">
          <div className="space-y-1.5">
            {order.map((card) => {
              const hidden = state.hidden_cards.includes(card.key);
              return (
                <div key={card.key}
                     className="flex items-center gap-2 rounded-lg border border-white/[0.08]
                                bg-white/[0.02] px-3 py-2"
                     data-testid={`card-setting-${card.key}`}>
                  <span className={`flex-1 text-xs ${hidden ? 'text-white/30' : 'text-white/75'}`}>
                    {card.label}{card.wide ? ' · full width' : ''}
                  </span>
                  <button onClick={() => move(card.key, -1)} className="text-white/30
                          hover:text-white transition-colors"
                          data-testid={`card-up-${card.key}`}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => move(card.key, 1)} className="text-white/30
                          hover:text-white transition-colors"
                          data-testid={`card-down-${card.key}`}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleCard(card.key)}
                          className={hidden ? 'text-white/30' : 'text-amber-200/80'}
                          data-testid={`card-toggle-${card.key}`}>
                    {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default CompanionSettings;
